import React, { useEffect, useState } from "react";
import { Alert, Modal, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import ReactSelect from "react-select";
import { Icon } from "../../utils/iconHelper";
import BatchManagementService, {
  BatchListItem,
  BatchLookups,
} from "../../_services/BatchManagementService";
import BatchFormPanel from "./components/BatchFormPanel";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./batch-management.css";

const BatchManagementPage: React.FC = () => {
  useDocumentTitle("Batch Management");
  const history = useHistory();

  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const [searchTerm, setSearchTerm] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [termFilter, setTermFilter] = useState("");
  const [batchTypeFilter, setBatchTypeFilter] = useState("");
  const [parentFilter, setParentFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const [lookups, setLookups] = useState<BatchLookups>({
    isProgramBased: false,
    schools: [],
    years: [],
    batchTypes: [],
    parents: [],
    programs: [],
    programYears: [],
    programSchools: [],
    terms: [],
    courses: [],
    tags: [],
  });

  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [showFormPanel, setShowFormPanel] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState<BatchListItem | null>(null);

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [currentPage, statusFilter, sortBy, sortDir]);

  const loadLookups = async () => {
    const res = await BatchManagementService.getLookups();
    if (res.success) setLookups(res.data);
  };

  const fetchBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await BatchManagementService.searchBatches({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        schoolId: schoolFilter ? Number(schoolFilter) : undefined,
        programId: programFilter ? Number(programFilter) : undefined,
        courseId: courseFilter ? Number(courseFilter) : undefined,
        yearId: yearFilter ? Number(yearFilter) : undefined,
        currentTermId: termFilter ? Number(termFilter) : undefined,
        batchTypeId: batchTypeFilter ? Number(batchTypeFilter) : undefined,
        parentId: parentFilter ? Number(parentFilter) : undefined,
        tagId: tagFilter ? Number(tagFilter) : undefined,
        includeInactive: statusFilter === "all" || statusFilter === "inactive" ? true : undefined,
        sortBy,
        sortDir,
      });
      if (res.success) {
        let rows = res.data.batches;
        if (statusFilter === "inactive") rows = rows.filter((b) => b.activeStatus !== 1);
        setBatches(rows);
        setTotalCount(res.data.pagination.total);
      } else {
        setError("Failed to fetch batches");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch batches");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchBatches();
  };

  const handleReset = () => {
    setSearchTerm("");
    setSchoolFilter("");
    setProgramFilter("");
    setCourseFilter("");
    setYearFilter("");
    setTermFilter("");
    setBatchTypeFilter("");
    setParentFilter("");
    setTagFilter("");
    setStatusFilter("active");
    setCurrentPage(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const sortIcon = (column: string) => {
    const active = sortBy === column;
    const isAsc = active && sortDir === "asc";
    return (
      <span className={`bm-sort-icon${active ? " bm-sort-active" : ""}`}>
        {active ? (
          isAsc ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />
        ) : (
          <Icon.ChevronDown size={12} style={{ opacity: 0.4 }} />
        )}
      </span>
    );
  };

  /** Classify a batch by fill ratio for the strength progress bar. */
  const strengthClass = (max: number | null, current: number) => {
    if (!max || max <= 0) return "";
    const ratio = current / max;
    if (current >= max || ratio >= 0.95) return "bm-fill-danger";
    if (ratio >= 0.7) return "bm-fill-warning";
    return "bm-fill-open";
  };

  const SKELETON_ROW_COUNT = 6;

  const confirmDelete = async () => {
    if (!deletingBatch) return;
    const res = await BatchManagementService.deleteBatch(deletingBatch.id);
    if (res.success) {
      setSuccess(`Batch "${deletingBatch.name}" deactivated`);
      setShowDeleteModal(false);
      setDeletingBatch(null);
      fetchBatches();
    } else {
      setError(res.message || "Failed to delete batch");
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Filter terms in the term filter dropdown by selected program (if program-based)
  const termFilterOptions = lookups.isProgramBased && programFilter
    ? lookups.terms.filter((t) => t.programId === Number(programFilter))
    : lookups.terms;

  // Filter intakes by selected program (via program_year_properties)
  const yearFilterOptions = lookups.isProgramBased && programFilter
    ? (() => {
        const allowed = new Set(
          (lookups.programYears || [])
            .filter((py) => py.programId === Number(programFilter))
            .map((py) => py.yearId)
        );
        return lookups.years.filter((y) => allowed.has(y.id));
      })()
    : lookups.years;

  // Filter programs by selected school/BU (via program_school_mapping)
  const programFilterOptions = lookups.isProgramBased && schoolFilter
    ? (() => {
        const allowed = new Set(
          (lookups.programSchools || [])
            .filter((ps) => ps.schoolId === Number(schoolFilter))
            .map((ps) => ps.programId)
        );
        return lookups.programs.filter((p) => allowed.has(p.id));
      })()
    : lookups.programs;

  return (
    <div className="bm-page">
      <div className="bm-header">
        <h1>Batch Management</h1>
        <div className="bm-header-actions">
          <button
            className="bm-btn-seat-availability"
            onClick={() => history.push("/seat-availability")}
            title="Live capacity across all active batches"
          >
            <Icon.Layers size={16} />
            Seat Availability
          </button>
          <button className="bm-btn-create" onClick={() => { setEditingBatchId(null); setShowFormPanel(true); }}>
            <Icon.Plus size={16} />
            Create New Batch
          </button>
        </div>
      </div>

      {error && (
        <div className="bm-alert bm-alert-error">
          <span>{error}</span>
          <button className="bm-alert-dismiss" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="bm-alert bm-alert-success">
          <span>{success}</span>
          <button className="bm-alert-dismiss" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="bm-filters">
        <input
          className="bm-search-input"
          type="text"
          placeholder="Search by name, code or short code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <select
          className="bm-filter-select"
          value={schoolFilter}
          onChange={(e) => {
            const v = e.target.value;
            setSchoolFilter(v);
            // If the currently-selected program isn't mapped to this school, clear it
            if (v && programFilter) {
              const allowed = new Set(
                (lookups.programSchools || [])
                  .filter((ps) => ps.schoolId === Number(v))
                  .map((ps) => ps.programId)
              );
              if (!allowed.has(Number(programFilter))) {
                setProgramFilter("");
                setTermFilter("");
              }
            }
          }}
        >
          <option value="">All Schools</option>
          {lookups.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {lookups.isProgramBased ? (
          <>
            <div className="bm-filter-typeahead">
              <ReactSelect
                isClearable
                placeholder="All Programs"
                options={programFilterOptions.map((p) => ({
                  value: String(p.id),
                  label: p.code ? `${p.name} [${p.code}]` : p.name,
                }))}
                value={
                  programFilter
                    ? (() => {
                        const p = lookups.programs.find((pr) => String(pr.id) === programFilter);
                        return p ? { value: String(p.id), label: p.code ? `${p.name} [${p.code}]` : p.name } : null;
                      })()
                    : null
                }
                onChange={(opt: any) => {
                  const newProgramFilter = opt ? opt.value : "";
                  setProgramFilter(newProgramFilter);
                  setTermFilter("");
                  // If the currently-selected intake isn't valid for this program, clear it
                  if (newProgramFilter && yearFilter) {
                    const allowed = new Set(
                      (lookups.programYears || [])
                        .filter((py) => py.programId === Number(newProgramFilter))
                        .map((py) => py.yearId)
                    );
                    if (!allowed.has(Number(yearFilter))) setYearFilter("");
                  }
                }}
                styles={{
                  control: (base) => ({ ...base, minHeight: 38, borderRadius: 6 }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
              />
            </div>
            <select className="bm-filter-select" value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
              <option value="">All Terms</option>
              {termFilterOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </>
        ) : (
          <select className="bm-filter-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
            <option value="">All Courses</option>
            {lookups.courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="bm-filter-select" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
          <option value="">All Intakes</option>
          {yearFilterOptions.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
        <select className="bm-filter-select" value={batchTypeFilter} onChange={(e) => setBatchTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {lookups.batchTypes.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="bm-filter-select" value={parentFilter} onChange={(e) => setParentFilter(e.target.value)}>
          <option value="">Any Parent</option>
          {lookups.parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {lookups.tags.length > 0 && (
          <select className="bm-filter-select" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">All Tags</option>
            {lookups.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <select className="bm-filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
        <div className="bm-filter-actions">
          <button className="bm-btn-apply" onClick={handleSearch}>Apply</button>
          <button className="bm-btn-reset" onClick={handleReset}>Reset</button>
          <span className="bm-count"><strong>{totalCount}</strong> batches</span>
        </div>
      </div>

      <div className="bm-table-container">
        <div className="bm-table-scroll">
          <table className="bm-table">
            <thead>
              <tr>
                <th className="bm-sortable" onClick={() => handleSort("name")}>
                  Batch{sortIcon("name")}
                </th>
                <th className="bm-sortable" style={{ width: 240 }} onClick={() => handleSort("school")}>
                  BU &amp; {lookups.isProgramBased ? "Program" : "Course"}{sortIcon("school")}
                </th>
                <th className="bm-sortable" style={{ width: 160 }} onClick={() => handleSort("year")}>
                  Intake / Type{sortIcon("year")}
                </th>
                <th style={{ width: 180 }}>Strength</th>
                <th style={{ width: 200 }}>Tags</th>
                <th className="bm-sortable" style={{ width: 90 }} onClick={() => handleSort("status")}>
                  Status{sortIcon("status")}
                </th>
                <th style={{ width: 120, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                  <tr key={`bm-skel-${i}`}>
                    <td>
                      <span className="bm-skel" style={{ width: 140 }} />
                      <div style={{ marginTop: 6 }}>
                        <span className="bm-skel" style={{ width: 80, height: 10 }} />
                      </div>
                    </td>
                    <td>
                      <span className="bm-skel" style={{ width: 120 }} />
                      <div style={{ marginTop: 6 }}>
                        <span className="bm-skel" style={{ width: 90, height: 10 }} />
                      </div>
                    </td>
                    <td>
                      <span className="bm-skel" style={{ width: 90 }} />
                      <div style={{ marginTop: 6 }}>
                        <span className="bm-skel" style={{ width: 60, height: 10 }} />
                      </div>
                    </td>
                    <td>
                      <div style={{ minWidth: 130 }}>
                        <span className="bm-skel" style={{ width: 70, marginBottom: 4 }} />
                        <span className="bm-skel bm-skel-bar" />
                      </div>
                    </td>
                    <td>
                      <span className="bm-skel" style={{ width: 100, borderRadius: 999, height: 16 }} />
                    </td>
                    <td>
                      <span className="bm-skel" style={{ width: 56, borderRadius: 999, height: 16 }} />
                    </td>
                    <td>
                      <span className="bm-skel" style={{ width: 90, height: 22 }} />
                    </td>
                  </tr>
                ))
              ) : batches.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="bm-empty">
                      <Icon.Layers size={36} style={{ color: "var(--text-faint)" }} />
                      <h5>No batches found</h5>
                      <p>Try adjusting filters or create a new batch.</p>
                      <button
                        className="bm-btn-create"
                        style={{ marginTop: 8 }}
                        onClick={() => { setEditingBatchId(null); setShowFormPanel(true); }}
                      >
                        <Icon.Plus size={14} /> Create batch
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                batches.map((b) => {
                  const cur = b.currentStrength ?? 0;
                  const max = b.maxStrength;
                  const pct = max && max > 0 ? Math.min(100, Math.round((cur / max) * 100)) : null;
                  return (
                    <tr key={b.id}>
                      {/* Batch */}
                      <td>
                        <div
                          className="bm-batch-name"
                          onClick={() => history.push(`/batch-management/${b.id}`)}
                        >
                          {b.name || "—"}
                        </div>
                        <div className="bm-batch-sub">
                          {b.batchCode && <span className="bm-batch-code">{b.batchCode}</span>}
                          {b.batchCode && <span style={{ margin: "0 6px" }}>·</span>}
                          <span>ID {b.id}</span>
                        </div>
                      </td>
                      {/* BU & Course/Program */}
                      <td>
                        <div className="bm-stack-primary">
                          {lookups.isProgramBased ? (b.programName || "—") : (b.courseName || "—")}
                        </div>
                        <div className="bm-stack-secondary">{b.schoolName || "—"}</div>
                      </td>
                      {/* Intake / Type */}
                      <td>
                        <div className="bm-stack-primary">{b.yearName || "—"}</div>
                        {b.batchTypeName && (
                          <div style={{ marginTop: 4 }}>
                            <span className="bm-type-pill">{b.batchTypeName}</span>
                          </div>
                        )}
                      </td>
                      {/* Strength */}
                      <td>
                        {max && max > 0 ? (
                          <div className="bm-strength">
                            <div className="bm-strength-label">
                              <span>
                                {cur}<span className="bm-strength-cap"> / {max}</span>
                              </span>
                              <span className="bm-strength-cap">{pct}%</span>
                            </div>
                            <div className="bm-strength-bar">
                              <div
                                className={`bm-strength-fill ${strengthClass(max, cur)}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="bm-cell-muted">
                            {cur} <span style={{ fontSize: 11 }}>(no cap)</span>
                          </span>
                        )}
                      </td>
                      {/* Tags */}
                      <td>
                        {b.tags && b.tags.length > 0 ? (
                          <div className="bm-tags">
                            {b.tags.slice(0, 4).map((t) => (
                              <span key={t.id} className="bm-tag-chip">{t.name}</span>
                            ))}
                            {b.tags.length > 4 && (
                              <span className="bm-tag-more">+{b.tags.length - 4}</span>
                            )}
                          </div>
                        ) : (
                          <span className="bm-cell-muted">—</span>
                        )}
                      </td>
                      {/* Status */}
                      <td>
                        <span className={`bm-status-pill ${b.activeStatus === 1 ? "bm-status-active" : "bm-status-inactive"}`}>
                          {b.activeStatus === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {/* Actions */}
                      <td>
                        <div className="bm-actions">
                          <button
                            className="bm-btn-action"
                            onClick={() => history.push(`/batch-management/${b.id}`)}
                            title="View batch"
                          >
                            <Icon.Eye size={14} />
                          </button>
                          <button
                            className="bm-btn-action bm-btn-edit"
                            onClick={() => { setEditingBatchId(b.id); setShowFormPanel(true); }}
                            title="Edit batch"
                          >
                            <Icon.Edit2 size={14} />
                          </button>
                          <button
                            className="bm-btn-action bm-btn-delete"
                            onClick={() => { setDeletingBatch(b); setShowDeleteModal(true); }}
                            title="Deactivate batch"
                          >
                            <Icon.Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalPages > 1 && (
          <div className="bm-pagination">
            <span className="bm-pagination-info">
              Showing {startItem}&ndash;{endItem} of {totalCount}
            </span>
            <div className="bm-pagination-controls">
              <button className="bm-btn-page" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
              <span className="bm-page-info">Page {currentPage} of {totalPages}</span>
              <button className="bm-btn-page" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <BatchFormPanel
        show={showFormPanel}
        onHide={() => setShowFormPanel(false)}
        batchId={editingBatchId}
        lookups={lookups}
        onSuccess={() => {
          setShowFormPanel(false);
          setSuccess(editingBatchId ? "Batch updated successfully" : "Batch created successfully");
          setEditingBatchId(null);
          fetchBatches();
          loadLookups(); // refresh parents list with new entry
        }}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to deactivate this batch?</p>
          {deletingBatch && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingBatch.name}</strong>
              {deletingBatch.batchCode && <><br /><small className="text-muted">Code: {deletingBatch.batchCode}</small></>}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Deactivate</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BatchManagementPage;
